<template>
  <div
    class="d-top-menu-container"
    :class="{
      'fixed': isFixed,
      'scroll-aware': !ignoreScrolling,
      'transparent': transparent,
    }"
  >
    <nav
      class="menu top-menu"
      :class="!isFixed ? topMenuTheme : ''"
    >
      <div class="logo-container">
        <DBackButton
          v-if="showBackButton"
          :button-class="isFixed ? 'button-grey-medium' : 'button-translucent'"
          :root-url="homeUrl"
        />

        <a
          class="top-menu-discovery-logo"
          :class="logoClass"
          :href="homeUrl"
          title="Home"
        />
      </div>

      <div class="links">
        <slot name="links" />
      </div>

      <div class="right-actions">
        <div v-if="!avoidSearch">
          <div v-if="isMobile">
            <a
              class="space-right-1em"
              @click="openMobileSearchCard"
            >
              <DuiIconSearch
                class="icon-lg mobile-search-icon"
                :class="mobileSearchIconClass"
              />
            </a>

            <DMobileCard
              store-close-action="Cards/closeMobileSearch"
              store-display-key="Cards/get-mobile-search-display-state"
            >
              <template slot="card-header">
                <h2>Search</h2>
              </template>
              <template slot="card-body">
                <div class="grid-flex grid-middle grid-no-response space-bottom-1em space-top-1em">
                  <div class="grid-item-shrink">
                    <DuiIconSearch
                      class="icon-lg space-right-1em"
                      style="margin-top: -4px;"
                    />
                  </div>
                  <div class="grid-item">
                    <input
                      ref="mobileSearchInput"
                      v-model="searchText"
                      class="input-full no-border"
                      placeholder="Search..."
                      type="text"
                      @input="performSearchMobile"
                    >
                  </div>
                  <div
                    v-if="!!searchText"
                    class="grid-item-shrink"
                  >
                    <a @click="clearSearch">clear</a>
                  </div>
                </div>

                <DSearchResults
                  v-if="!!searchText"
                  :always-displayed="true"
                />
              </template>
            </DMobileCard>
          </div>

          <div
            v-else
            class="search-container"
            @click.stop.prevent="showSearchResults"
          >
            <DuiSearchInput
              v-model="searchText"
              placeholder="Search..."
              @input="performSearch"
            />

            <DSearchResults
              ref="searchResults"
              :is-popup="true"
            />
          </div>
        </div>

        <slot name="right" />
      </div>
    </nav>
  </div>
</template>

<script>
import DBackButton from '@d-components/d-back-button/d-back-button.vue';
import DMobileCard from '@d-components/d-mobile-card/d-mobile-card.vue';
import DSearchResults from '@d-components/d-search-results/d-search-results.vue';
import mobileMixin from '@d-mixins/mobile';
import SASS from '@dui/helpers/sass';
import URLS from '@d-shared/urls/urls';

const LOGO_CLASSES = Object.freeze({
  dark: 'discovery-menu-logo-dark',
  white: 'discovery-menu-logo-white',
});

const WHITE_THEME = 'menu-white-theme';

export default {
  components: {
    DBackButton,
    DMobileCard,
    DSearchResults,
  },
  mixins: [
    mobileMixin,
  ],
  props: {
    avoidSearch: {
      type: Boolean,
      default: false,
    },
    ignoreScrolling: {
      type: Boolean,
      default: false,
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    topMenuTheme: {
      type: String,
      default: null,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    homeUrl: `${ window.location.protocol }//${ window.location.host }${ URLS.app.home }`,
    isFixed: false,
    searchText: '',
  }),
  computed: {
    logoClass() {
      if (this.isMobile) {
        if (this.transparent && this.topMenuTheme !== WHITE_THEME) {
          return LOGO_CLASSES.dark;
        }

        return LOGO_CLASSES.white;
      }

      if (!this.isFixed && this.topMenuTheme === WHITE_THEME) {
        return LOGO_CLASSES.white;
      }

      return LOGO_CLASSES.dark;
    },

    mobileSearchCardDisplayState() {
      if (this.avoidSearch) {
        return null;
      }

      return this.$store.getters['Cards/get-mobile-search-display-state'];
    },

    mobileSearchIconClass() {
      if (!this.isMobile) {
        return '';
      }

      if (!this.transparent || this.topMenuTheme === WHITE_THEME) {
        return 'icon-white';
      }

      return '';
    },
  },
  watch: {
    mobileSearchCardDisplayState: function() {
      if (!this.mobileSearchCardDisplayState) {
        this.clearSearch();
      }
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  mounted() {
    this.init();
  },
  methods: {
    addEventListeners() {
      window.addEventListener('scroll', this.checkFixedStatus);
    },

    checkFixedStatus() {
      const isFixed = !this.isMobile && (window.scrollY > 0);

      if (this.isFixed === isFixed) {
        return;
      }

      this.$set(this, 'isFixed', isFixed);
    },

    clearSearch() {
      this.$set(this, 'searchText', '');
    },

    init() {
      if (this.ignoreScrolling) {
        return;
      }

      this.addEventListeners();
      this.checkFixedStatus();
    },

    openMobileSearchCard() {
      if (!this.isMobile) {
        return;
      }

      this.$store.dispatch('Cards/openMobileSearch');
    },

    performSearch() {
      if (this.isMobile || !this.searchText) {
        this.$refs.searchResults.toggleDisplayed(false);
        return;
      }

      this.$store.dispatch('Search/searchFor', this.searchText);
      this.$refs.searchResults.toggleDisplayed(true);
    },

    performSearchMobile() {
      if (!this.isMobile || !this.searchText) {
        return;
      }

      this.$store.dispatch('Search/searchFor', this.searchText);
    },

    removeEventListeners() {
      window.removeEventListener('scroll', this.checkFixedStatus);
    },

    showSearchResults() {
      if (this.isMobile || !this.searchText) {
        return;
      }

      this.$refs.searchResults.toggleDisplayed(true);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-top-menu-container {
  position: relative;
  z-index: 2;

  @include screenLessThanCustom($menu-md-breakpoint) {
    background-color: getColor($black);

    &.transparent {
      background-color: transparent;
    }

    .search-container {
      display: none;
    }
  }

  @include screenMoreThanCustom($menu-md-breakpoint) {
    &.scroll-aware {
      left: 0;
      position: fixed;
      top: 0;
      transform: translate3d(0,0,0);
      width: 100%;
      z-index: $z-index-sticky-actions;

      @include transition((
        background-color,
        box-shadow,
      ));

      + * {
        padding-top: ($top-menu-height-desktop - $_1em);
      }

      &.fixed {
        background-color: getColor($white);
        box-shadow: $box-shadow-base;
      }
    }
  }

  .top-menu {
    margin: 0 $_2em;

    @include screenMoreThan(md) {
      margin: 0 auto;
      max-width: $layout-width-lg;
    }
  }

  .search-container {
    display: flex;
    flex: none;
    margin-right: $_1em;
    position: relative;
    width: 180px;

    @include screenMoreThan(md) {
      width: 200px;
    }

    .dui-search-input,
    .dui-search-input input {
      font-size: $font-size-sm;
    }

    .dui-search-input {
      border-radius: $_3em;
      padding: 0;

      input {
        height: ($_4em - (2 * $border-width-base));
      }

      .placeholder {
        left: $_1-5em;
      }

      .button-icon,
      .search-icon {
        right: $_1-5em;
      }
    }

    @include screenMoreThan(sm) {
      .d-search-results {
        left: 50%;
        position: absolute;
        top: calc(100% + #{ $_1em });
        transform: translateX(-50%);
        width: 300px;
      }
    }
  }
}
</style>
